import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { faCog, faExpand, faFileSignature, faFileImage, faUsers } from '@fortawesome/free-solid-svg-icons';
import type { ViewId, ViewModelId } from 'balus-common';
import { useNotifyHuddleMessageHandler } from '@view-model/application/notification';
import { ViewSetting } from '@view-model/domain/view';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { Size } from '@view-model/models/common/basic';
import { ForeignObjectAutosize2 } from '@view-model/models/common/components/ForeignObjectAutosize';
import { useViewOperation } from '@view-model/ui/components/View/ViewContext';
import { ViewSettingModal } from '@view-model/ui/components/View/ViewSettingModal';
import { MenuButton } from '@view-model/ui/components/View/ViewTitleMenu/MenuButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AssetUploadModal } from '../AssetUploadModal';
import { useAtomValue } from 'jotai';
import { contentTransformAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { ColorMenuButton } from '../ViewTitleMenu/ColorMenuButton';
import type { ThemeColor } from '@view-model/models/common/color';
import { useGroupLicensePermissions } from '@group/License';
import { useUserType } from '@framework/auth/authStatus';
import { useTranslation } from 'react-i18next';
type Props = {
  isOpen: boolean;
  viewRect: Rect;
  viewModelId: ViewModelId;
  viewId: ViewId;
  viewSetting: ViewSetting;
  onDelete(): void;
  onFocusView(): void;
  onClose(): void;
  onExportViewAsImage(): void;
  viewThemeColor: ThemeColor;
};

const TOP_MARGIN = -16;
const RIGHT_MARGIN = 64;

export const ViewPopupMenu: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const {
    isOpen,
    viewRect,
    viewModelId,
    viewId,
    viewSetting,
    onDelete,
    onFocusView,
    onExportViewAsImage,
    viewThemeColor,
  } = props;
  const permissions = useGroupLicensePermissions();
  const canUploadFile = useUserType() !== 'anonymous';
  const [popupMenuSize, setPopupMenuSize] = useState<Size>(Size.empty);
  const popupMenuPosition = viewRect.topRight().addXY(-popupMenuSize.width - RIGHT_MARGIN, 0);

  // ビューの設定
  const [showSettingModal, setShowSettingModal] = useState(false);
  const rootNode = useRef<HTMLElement>(document.querySelector('#root'));
  const handleOpenSettingModal = useCallback(() => {
    setShowSettingModal(true);
  }, []);
  const handleCloseSettingModal = useCallback(() => setShowSettingModal(false), []);

  const viewAdapter = useViewOperation();
  const handleShowNodeCreatedUserChanged = useCallback(
    async (checked: boolean, isLocked: boolean, isPositionFixed: boolean) => {
      const setting = new ViewSetting(checked, isLocked, isPositionFixed);
      await viewAdapter.handleSettingChanged(setting);
    },
    [viewAdapter]
  );

  const handleChangeColor = useCallback(
    (color: ThemeColor) => {
      viewAdapter.handleChangingViewColorConfirmed(color, viewThemeColor);
    },
    [viewThemeColor, viewAdapter]
  );

  // 強制的に参加者を集める
  const notifyHuddle = useNotifyHuddleMessageHandler(viewModelId, viewId);
  const handleHuddle = useCallback(() => {
    notifyHuddle();
  }, [notifyHuddle]);

  const propsRef = useRef<Props>(props);
  propsRef.current = props;

  useEffect(() => {
    // ポップアップメニューを表示した場合、どこかをクリックしたら閉じるようにする（メニューをクリックした場合も含めて）
    if (rootNode.current) {
      const node = rootNode.current;
      const handleClick = () => {
        if (propsRef.current.isOpen) {
          propsRef.current.onClose();
        }
      };
      node.addEventListener('click', handleClick);

      return () => {
        node.removeEventListener('click', handleClick);
      };
    }
  }, []);

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const isFileUploadDisabled = permissions?.fileUpload === false;
  const handleFileUpload = useCallback(() => {
    if (isFileUploadDisabled) return;
    setShowFileUploadModal(true);
  }, [isFileUploadDisabled]);
  const closeFileUpload = useCallback(() => setShowFileUploadModal(false), []);

  const handleExportViewAsImage = useCallback(() => {
    if (!permissions?.imageExport) return;
    onExportViewAsImage();
  }, [onExportViewAsImage, permissions]);

  const contentTransform = useAtomValue(contentTransformAtom);
  const scale = (1 / contentTransform.k) * 0.3; // メニューを常に同じ大きさで表示するために補正する
  const matrix = new DOMMatrix()
    .translateSelf(popupMenuPosition.x, popupMenuPosition.y)
    .scaleSelf(scale, scale, 1, popupMenuSize.width, 0)
    .translateSelf(0, TOP_MARGIN);

  return (
    <>
      {isOpen && (
        <ForeignObjectAutosize2 transform={matrix.toString()} onSizeChange={setPopupMenuSize}>
          <div
            className="flex flex-col rounded-2xl bg-white py-4 text-5xl shadow-lg"
            style={{
              visibility: popupMenuSize.isEmpty() ? 'hidden' : 'visible',
            }}
          >
            <MenuButton icon={faCog} title={t('canvas.viewSetting.label')} onClick={handleOpenSettingModal} />
            {!viewSetting.isLocked && (
              <>
                <MenuButton icon={faExpand} title={t('canvas.viewPopupMenu.focusView')} onClick={onFocusView} />
                <MenuButton icon={faUsers} title={t('canvas.viewPopupMenu.huddle')} onClick={handleHuddle} />
                {canUploadFile && (
                  <MenuButton
                    icon={faFileSignature}
                    title={t('canvas.viewPopupMenu.fileUpload')}
                    onClick={handleFileUpload}
                    disabled={isFileUploadDisabled}
                  />
                )}
                <MenuButton
                  icon={faFileImage}
                  title={t('canvas.viewPopupMenu.exportImage')}
                  onClick={handleExportViewAsImage}
                  disabled={!permissions?.imageExport}
                />
                <ColorMenuButton onChangeColor={handleChangeColor} />
                <MenuButton icon={faTrashAlt} title={t('common.action.delete')} onClick={onDelete} color="red" />
              </>
            )}
          </div>
        </ForeignObjectAutosize2>
      )}

      <ViewSettingModal
        isOpen={showSettingModal}
        viewSetting={viewSetting}
        onClose={handleCloseSettingModal}
        onShowNodeCreatedUserChanged={handleShowNodeCreatedUserChanged}
      />

      {showFileUploadModal && <AssetUploadModal viewId={viewId} viewRect={viewRect} onClose={closeFileUpload} />}
    </>
  );
};
